import { combineReducers } from "@reduxjs/toolkit";

import { searchCriteriaReducer as buildSearchCriteriaReducer } from "common/reducer/searchCriteriaReducer";
import { TopicLatestActionTypes } from "domains/knowledgeFeed/actionTypes";
import type { TopicKnowledgeFeedPackage, TopicKnowledgeFeedState } from "domains/knowledgeFeed/types";
import { findMatchingCriteriaAndOthers } from "domains/publication/reducer";
import type { FullTopicPublicationSearchCriteria } from "domains/publication/types";
import { addTopicSelectionReset, addUserOrMapChangeReset } from "domains/topic/reducerHelpers";
import type { KnowledgeFeedAction } from "entities/knowledgeFeed/actionTypes";
import { KnowledgeFeedActionTypes } from "entities/knowledgeFeed/actionTypes";

const initialState: TopicKnowledgeFeedState = {
  forTopic: {},
  searchCriteria: {
    content_type: "",
    search: "",
    language: "",
    publication_type: "",
    year: "",
    role: "",
    limit: 14,
    offset: 0,
  },
};

// Convenience function to capture the generic type args
const findKnowledgeFeedPackageAndOthers = (
  forTopic: TopicKnowledgeFeedState["forTopic"],
  searchCriteria: FullTopicPublicationSearchCriteria,
) =>
  findMatchingCriteriaAndOthers<TopicKnowledgeFeedPackage, FullTopicPublicationSearchCriteria>(
    forTopic,
    searchCriteria,
  );

const knowledgeFeedForTopicReducer = (
  state = initialState.forTopic,
  action: KnowledgeFeedAction,
): TopicKnowledgeFeedState["forTopic"] => {
  switch (action.type) {
    case KnowledgeFeedActionTypes.FETCH_FOR_TOPIC: {
      const { offset: _offset, limit: _limit, ...searchCriteria } = action.payload;
      const [matchingKnowledgeFeedPackage, others] = findKnowledgeFeedPackageAndOthers(state, searchCriteria);
      const newKnowledgeFeedPackage: TopicKnowledgeFeedPackage = {
        ...matchingKnowledgeFeedPackage,
        isFetching: true,
        hasNextPage: true,
        data: matchingKnowledgeFeedPackage?.data ?? [],
        searchCriteria,
      };
      return {
        ...state,
        [searchCriteria.id]: [...others, newKnowledgeFeedPackage],
      };
    }
    case KnowledgeFeedActionTypes.FETCH_FOR_TOPIC_FAILURE: {
      const { searchCriteria } = action.meta;
      const [matchingKnowledgeFeedPackage, others] = findKnowledgeFeedPackageAndOthers(state, searchCriteria);
      const newKnowledgeFeedPackage: TopicKnowledgeFeedPackage = {
        ...matchingKnowledgeFeedPackage,
        isFetching: false,
        hasNextPage: false,
        data: matchingKnowledgeFeedPackage?.data ?? [],
        searchCriteria,
      };
      return {
        ...state,
        [searchCriteria.id]: [...others, newKnowledgeFeedPackage],
      };
    }
    case KnowledgeFeedActionTypes.FETCH_FOR_TOPIC_SUCCESS: {
      const { data } = action.payload;
      const { offset, limit, ...searchCriteria } = action.meta;
      const [matchingKnowledgeFeedPackage, others] = findKnowledgeFeedPackageAndOthers(state, searchCriteria);
      const dataToKeep = matchingKnowledgeFeedPackage?.data.slice(0, offset) ?? [];
      const newKnowledgeFeedPackage: TopicKnowledgeFeedPackage = {
        ...matchingKnowledgeFeedPackage,
        isFetching: false,
        hasNextPage: data.length >= limit,
        data: dataToKeep.concat(data.map(({ id }) => id)),
        searchCriteria,
      };
      return {
        ...state,
        [searchCriteria.id]: [...others, newKnowledgeFeedPackage],
      };
    }
    default:
      return state;
  }
};

const searchCriteriaReducer = buildSearchCriteriaReducer(
  initialState.searchCriteria,
  TopicLatestActionTypes.APPLY_SEARCH_CRITERIA,
  TopicLatestActionTypes.CLEAR_SEARCH_CRITERIA,
);

export const knowledgeFeedReducer = combineReducers({
  forTopic: addUserOrMapChangeReset(knowledgeFeedForTopicReducer, initialState.forTopic),
  searchCriteria: addTopicSelectionReset(searchCriteriaReducer, initialState.searchCriteria),
});
